// src/Helpers/soundAlerts.js

let audioCtx = null;

function getAudioContext() {
  if (!audioCtx && typeof window !== 'undefined') {
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    if (AudioContextClass) {
      audioCtx = new AudioContextClass();
    }
  }
  return audioCtx;
}

export function isAudioMuted() {
  if (typeof window === 'undefined') return false;
  return localStorage.getItem('pdv_sound_muted') === 'true';
}

export function setAudioMuted(muted) {
  if (typeof window === 'undefined') return;
  localStorage.setItem('pdv_sound_muted', muted ? 'true' : 'false');
}

export function toggleAudioMuted() {
  const current = isAudioMuted();
  setAudioMuted(!current);
  return !current;
}

export function initAudioUnlock() {
  if (typeof window === 'undefined') return;

  const unlock = () => {
    try {
      const ctx = getAudioContext();
      if (ctx && ctx.state === 'suspended') {
        ctx.resume();
      }
    } catch (e) {
      // Ignore
    }
  };

  window.addEventListener('click', unlock, { passive: true });
  window.addEventListener('touchstart', unlock, { passive: true });
}

// Automatically register unlock listeners on page load
if (typeof window !== 'undefined') {
  initAudioUnlock();
}

/**
 * Toca uma nota acústica com harmônicos simulando sino/chime
 */
function playBellNote(ctx, freq, startTime, duration = 0.5, volume = 0.3) {
  try {
    const now = startTime;

    // Oscilador fundamental (onda senoidal pura)
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(freq, now);

    // Oscilador harmônico (onda triangular para timbre de sino)
    const overtoneOsc = ctx.createOscillator();
    const overtoneGain = ctx.createGain();
    overtoneOsc.type = 'triangle';
    overtoneOsc.frequency.setValueAtTime(freq * 2, now);

    // Envelope de ganho com ataque suave (sem cliques) e decaimento exponencial
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.linearRampToValueAtTime(volume, now + 0.012);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + duration);

    overtoneGain.gain.setValueAtTime(0.0001, now);
    overtoneGain.gain.linearRampToValueAtTime(volume * 0.22, now + 0.012);
    overtoneGain.gain.exponentialRampToValueAtTime(0.0001, now + duration * 0.55);

    osc.connect(gain);
    gain.connect(ctx.destination);

    overtoneOsc.connect(overtoneGain);
    overtoneGain.connect(ctx.destination);

    osc.start(now);
    osc.stop(now + duration + 0.05);

    overtoneOsc.start(now);
    overtoneOsc.stop(now + duration * 0.55 + 0.05);
  } catch (err) {
    console.warn('Erro ao tocar nota de sino:', err);
  }
}

export function isKitchenAudioMuted() {
  if (typeof window === 'undefined') return false;
  return localStorage.getItem('kitchen_sound_muted') === 'true';
}

export function setKitchenAudioMuted(muted) {
  if (typeof window === 'undefined') return;
  localStorage.setItem('kitchen_sound_muted', muted ? 'true' : 'false');
}

export function toggleKitchenAudioMuted() {
  const current = isKitchenAudioMuted();
  setKitchenAudioMuted(!current);
  return !current;
}

const lastPlayTime = {
  newOrder: 0,
  waiterCall: 0,
  paymentCall: 0,
  statusChange: 0,
  kitchenDish: 0,
};

/**
 * Alerta de NOVO PEDIDO: Fanfarra ascendente de 4 notas (C5, E5, G5, C6)
 */
export function playNewOrderAlert() {
  if (isAudioMuted()) return;
  const nowMs = Date.now();
  if (nowMs - lastPlayTime.newOrder < 1500) return;
  lastPlayTime.newOrder = nowMs;

  try {
    const ctx = getAudioContext();
    if (!ctx) return;
    if (ctx.state === 'suspended') ctx.resume();

    const t = ctx.currentTime;
    playBellNote(ctx, 523.25, t + 0.00, 0.40, 0.35); // C5
    playBellNote(ctx, 659.25, t + 0.12, 0.40, 0.35); // E5
    playBellNote(ctx, 783.99, t + 0.24, 0.45, 0.38); // G5
    playBellNote(ctx, 1046.50, t + 0.38, 0.65, 0.42); // C6
  } catch (e) {
    console.warn('Erro em playNewOrderAlert:', e);
  }
}

/**
 * Alerta de CHAMAR GARÇOM: Sino duplo "Ding-Dong... Ding-Dong" (A5 -> F#5)
 */
export function playWaiterCallAlert() {
  if (isAudioMuted()) return;
  const nowMs = Date.now();
  if (nowMs - lastPlayTime.waiterCall < 1500) return;
  lastPlayTime.waiterCall = nowMs;

  try {
    const ctx = getAudioContext();
    if (!ctx) return;
    if (ctx.state === 'suspended') ctx.resume();

    const t = ctx.currentTime;
    // 1º Ding-Dong
    playBellNote(ctx, 880.00, t + 0.00, 0.45, 0.40); // A5
    playBellNote(ctx, 739.99, t + 0.22, 0.60, 0.40); // F#5

    // 2º Ding-Dong
    playBellNote(ctx, 880.00, t + 0.60, 0.45, 0.40); // A5
    playBellNote(ctx, 739.99, t + 0.82, 0.75, 0.45); // F#5
  } catch (e) {
    console.warn('Erro em playWaiterCallAlert:', e);
  }
}

/**
 * Alerta de PEDIR A CONTA: Chime brilhante de caixa / pagamento (B5, E6, G#6)
 */
export function playPaymentCallAlert() {
  if (isAudioMuted()) return;
  const nowMs = Date.now();
  if (nowMs - lastPlayTime.paymentCall < 1500) return;
  lastPlayTime.paymentCall = nowMs;

  try {
    const ctx = getAudioContext();
    if (!ctx) return;
    if (ctx.state === 'suspended') ctx.resume();

    const t = ctx.currentTime;
    playBellNote(ctx, 987.77, t + 0.00, 0.35, 0.35); // B5
    playBellNote(ctx, 1318.51, t + 0.12, 0.40, 0.38); // E6
    playBellNote(ctx, 1661.22, t + 0.26, 0.60, 0.40); // G#6
  } catch (e) {
    console.warn('Erro em playPaymentCallAlert:', e);
  }
}

/**
 * Alerta de MUDANÇA DE STATUS DE BOTÃO: Notificação sutil de 2 notas (E5 -> A5)
 */
export function playStatusChangeAlert() {
  if (isAudioMuted()) return;
  const nowMs = Date.now();
  if (nowMs - lastPlayTime.statusChange < 1000) return;
  lastPlayTime.statusChange = nowMs;

  try {
    const ctx = getAudioContext();
    if (!ctx) return;
    if (ctx.state === 'suspended') ctx.resume();

    const t = ctx.currentTime;
    playBellNote(ctx, 659.25, t + 0.00, 0.28, 0.25); // E5
    playBellNote(ctx, 880.00, t + 0.12, 0.45, 0.30); // A5
  } catch (e) {
    console.warn('Erro em playStatusChangeAlert:', e);
  }
}

/**
 * Alerta da COZINHA: Chegada de novos pratos na fila de preparo
 */
export function playKitchenDishAlert() {
  if (isKitchenAudioMuted()) return;
  const nowMs = Date.now();
  if (nowMs - lastPlayTime.kitchenDish < 1500) return;
  lastPlayTime.kitchenDish = nowMs;

  try {
    const ctx = getAudioContext();
    if (!ctx) return;
    if (ctx.state === 'suspended') ctx.resume();

    const t = ctx.currentTime;
    // Chime triplo ascendente e metálico de pedido na cozinha (C6, E6, G6)
    playBellNote(ctx, 1046.50, t + 0.00, 0.40, 0.45); // C6
    playBellNote(ctx, 1318.51, t + 0.15, 0.45, 0.45); // E6
    playBellNote(ctx, 1567.98, t + 0.32, 0.70, 0.50); // G6
  } catch (e) {
    console.warn('Erro em playKitchenDishAlert:', e);
  }
}

